import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  FileText, 
  Home, 
  Scale,
  Heart,
  AlertCircle,
  CheckCircle2,
  Car,
  Building2,
  Users,
  Loader2,
  Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotaryPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "notary",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        service_interest: "notary-services",
        message: formData.message || "Notary service inquiry from website"
      });
      toast.success("Thank you! We'll contact you shortly to schedule your appointment.");
      setFormData({ name: "", email: "", phone: "", service_interest: "notary", message: "" });
    } catch (error) {
      toast.error("Unable to submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: FileText,
      title: "General Notary Services",
      description: "Acknowledgments, jurats, oaths, affirmations, and copy certifications for all your document needs."
    },
    {
      icon: Home,
      title: "Loan Signings / Closings",
      description: "Professional loan signing agent services for mortgage refinances, purchases, and HELOCs."
    },
    {
      icon: Building2,
      title: "Real Estate Documents",
      description: "Deeds, contracts, affidavits, and all real estate transaction documents notarized on-site."
    },
    {
      icon: Scale,
      title: "Power of Attorney",
      description: "Witness and notarize general, durable, healthcare, and financial power of attorney documents."
    },
    {
      icon: Heart,
      title: "Marriage Certificates",
      description: "Officiate and notarize marriage certificates. Make your special day official with convenience."
    },
    {
      icon: AlertCircle,
      title: "After-Hours & Emergency",
      description: "Need a notary outside regular hours? We offer after-hours and emergency notary services."
    }
  ];

  const serviceAreas = [
    "Montgomery",
    "Prattville",
    "Millbrook",
    "Wetumpka",
    "Pike Road",
    "Birmingham",
    "Tuscaloosa",
    "Auburn",
    "Selma",
    "Troy"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="notary-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#9D9045]/20 text-[#9D9045] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Car className="h-4 w-4" />
                MOBILE NOTARY SERVICES
              </div>
              
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                OMW Notary
              </h1>
              
              <p className="text-2xl text-[#9D9045] font-semibold mb-4">
                "On My Way" — We Come to You!
              </p>
              
              <p className="text-lg text-gray-300 mb-8">
                Fast, reliable, professional mobile notary services for the River Region and surrounding areas. 
                Skip the office visit — we bring the notary to your home, office, or wherever you need us.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a href="tel:8772955905">
                  <Button size="lg" className="w-full sm:w-auto bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    (877) 295-5905 ext. 4
                  </Button>
                </a>
                <a href="#request-service">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                    Request Service
                  </Button>
                </a>
              </div>

              <p className="text-gray-400 text-sm mt-6">
                <span className="text-[#9D9045]">★</span> Serving Montgomery, Birmingham & Surrounding Areas
              </p>
            </div>

            {/* Logo/Image */}
            <div className="hidden lg:flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_biz-credit-school/artifacts/66h3mb7z_IMG_0275.png" 
                  alt="OMW Notary Logo" 
                  className="max-w-[300px] mx-auto"
                />
                <p className="text-white text-xl font-bold mt-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Sealed. Signed. Delivered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#9D9045] font-semibold text-sm tracking-wider uppercase">Our Services</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E3A5F] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mobile Notary Services
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              Professional notary services brought directly to you. Available for individuals, businesses, and legal professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="h-14 w-14 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center mb-4">
                  <service.icon className="h-7 w-7 text-[#1E3A5F]" />
                </div>
                <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Professionals */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f] rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  For Real Estate & Legal Professionals
                </h2>
                <p className="text-gray-300 mb-6">
                  We understand the demands of your industry. Our mobile notary services are designed 
                  to meet the fast-paced needs of:
                </p>
                <ul className="space-y-3">
                  {["Realtors & Real Estate Agents", "Title Companies", "Attorneys & Law Firms", "Mortgage Lenders", "Escrow Companies"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-white">
                      <CheckCircle2 className="h-5 w-5 text-[#9D9045]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Why Professionals Choose Us</h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#9D9045] mt-1" />
                    <span><strong className="text-white">Fast Response:</strong> We're on our way when you call</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star className="h-5 w-5 text-[#9D9045] mt-1" />
                    <span><strong className="text-white">Reliable:</strong> Timely and accurate document execution</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-[#9D9045] mt-1" />
                    <span><strong className="text-white">Flexible:</strong> We work around your client's schedule</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-[#9D9045] mt-1" />
                    <span><strong className="text-white">After-Hours:</strong> Available for emergency signings</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <MapPin className="h-12 w-12 text-[#9D9045] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Service Area
          </h2>
          <p className="text-gray-600 mb-8">
            Proudly serving the River Region and surrounding areas in Alabama
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, index) => (
              <span 
                key={index}
                className="bg-white px-4 py-2 rounded-full text-[#1E3A5F] font-medium shadow-sm"
              >
                {area}
              </span>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-6">
            Don't see your area? Call us — we may still be able to help!
          </p>
        </div>
      </section>

      {/* Request Service Form */}
      <section id="request-service" className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Request Notary Service
            </h2>
            <p className="text-gray-600 mt-3">
              Fill out the form below and we'll contact you to schedule your appointment.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="py-6"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="py-6"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="py-6"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your notary needs
                </label>
                <Textarea
                  placeholder="Type of document, preferred date/time, location, etc."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white py-6 text-lg"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Request Service"
                )}
              </Button>
            </form>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-[#1E3A5F] mb-4">Contact Us Directly</h3>
                <div className="space-y-4">
                  <a href="tel:8772955905" className="flex items-center gap-3 text-gray-700 hover:text-[#9D9045]">
                    <div className="h-10 w-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-[#1E3A5F]" />
                    </div>
                    <div>
                      <p className="font-medium">(877) 295-5905 ext. 4</p>
                      <p className="text-sm text-gray-500">Call or Text</p>
                    </div>
                  </a>
                  <a href="mailto:omwbusiness@consultant.com" className="flex items-center gap-3 text-gray-700 hover:text-[#9D9045]">
                    <div className="h-10 w-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-[#1E3A5F]" />
                    </div>
                    <div>
                      <p className="font-medium">omwbusiness@consultant.com</p>
                      <p className="text-sm text-gray-500">Email Us</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-[#1E3A5F] rounded-xl p-6 text-white">
                <h3 className="font-bold mb-4">Need Emergency Service?</h3>
                <p className="text-gray-300 mb-4">
                  We offer after-hours and emergency notary services. Call or text us anytime and we'll do our best to accommodate your urgent needs.
                </p>
                <a href="tel:8772955905">
                  <Button className="w-full bg-[#9D9045] hover:bg-[#8a7e3c] text-white">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </Button>
                </a>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-[#1E3A5F] mb-2">What to Have Ready</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                    Valid government-issued photo ID
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                    Documents ready for signature (unsigned)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                    All signers present at appointment
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#9D9045] text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sealed. Signed. Delivered.
          </p>
          <p className="text-white text-lg">
            OMW Notary — "On My Way" to serve you!
          </p>
        </div>
      </section>
    </div>
  );
};

export default NotaryPage;
